/**
 * F16.C: per-portal MSAL instance registry. Each `/c/${slug}` portal binds
 * to its firm's Entra tenant via `tenantBranding.lockedTenantId` and gets a
 * dedicated `PublicClientApplication` keyed by that tenant id.
 *
 * Why per-instance (decisions.md §D6):
 *   - Cleanest isolation. A token acquired under firm-A's authority cannot
 *     leak into firm-B's calls because each tenant uses a separate MSAL
 *     instance with a tenant-scoped `cacheKeyPrefix`.
 *   - Avoids `clearCache()` races where a token-acquire fires before
 *     the cache is cleared on a portal switch.
 *
 * Why NOT touch the existing singleton in `lib/graph/msalClient.ts`:
 *   - Several existing SPA surfaces (`/`, `/trust/sandbox`, `Diagnostics`)
 *     depend on `/common` authority + the default scopes. Keeping them
 *     working is non-negotiable.
 *
 * Cleanup: an instance lingers 5 minutes after the last consumer detaches
 * from the registry (`releaseInstance`). The timer is reset on every
 * re-acquire so a quick re-mount keeps the same instance.
 */

import {
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { GRAPH_SCOPES } from '../graph/msalClient';
import { PORTAL_SIGNIN_PENDING_KEY } from './portalSignInRedirect';

const IDLE_CLEANUP_MS = 5 * 60 * 1000;

interface RegistryEntry {
  instance: PublicClientApplication;
  refCount: number;
  cleanupTimer: ReturnType<typeof setTimeout> | null;
  initPromise: Promise<void>;
}

/** Module-level registry. Keyed by lockedTenantId (lower-cased GUID). */
const registry = new Map<string, RegistryEntry>();

function normalize(tenantId: string): string {
  return tenantId.trim().toLowerCase();
}

function buildConfig(tenantId: string): Configuration {
  const clientId = import.meta.env.VITE_MSAL_CLIENT_ID as string | undefined;
  const redirectUriEnv = import.meta.env.VITE_MSAL_REDIRECT_URI as
    | string
    | undefined;
  if (!clientId) {
    throw new Error('VITE_MSAL_CLIENT_ID is required to connect Microsoft 365.');
  }
  // Auto-derive redirect URI from current origin so the popup-poll stays
  // same-origin across dev ports (5091 / 5092 / 3091). See msalClient.ts
  // for the full rationale; same fix applied here for consistency.
  const computedRedirect = `${window.location.origin}/auth-redirect.html`;
  const redirectUri =
    redirectUriEnv && redirectUriEnv.startsWith(window.location.origin)
      ? redirectUriEnv
      : computedRedirect;
  return {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri,
    },
    cache: {
      // F16.J: switched from `memoryStorage` to `sessionStorage`.
      // memoryStorage was the original F16.C choice for cleanest
      // isolation, but it surfaced a Chromium-specific
      // `loginPopup`-hangs-forever bug: the popup's auth code arrives
      // but the opener's PCA never reconciles it (the in-memory state
      // map keyed against the opener's PCA instance is invisible to
      // MSAL's popup-polling correlation step). 1Password even
      // detected the credential exchange, but our React state never
      // observed a resolve/reject on the loginPopup promise.
      //
      // sessionStorage works the way MSAL docs document. Per-portal
      // isolation still holds because (a) each portal mounts a
      // separate PCA instance with its own `clientId`-keyed cache
      // entries, and (b) the registry guarantees one instance per
      // `lockedTenantId` so two portals in two tabs never share a
      // PublicClientApplication.
      cacheLocation: 'sessionStorage',
    },
    system: {
      // F16.J: bump the BroadcastChannel popup-bridge timeout from
      // MSAL v5's default 60s to 180s. With cached creds + SSO the
      // round-trip is < 5s, but the first-time consent + tenant picker
      // takes 30-60s of human-time and slow networks can blow past
      // the default cap with `BrowserAuthError: timed_out`
      // (https://aka.ms/msal.js.errors#timed_out).
      //
      // In MSAL v5 the per-platform timeout knobs are
      // `popupBridgeTimeout` (popup mode, used here) and
      // `iframeBridgeTimeout` (silent SSO iframes). The old v3
      // `windowHashTimeout`/`navigateFrameWait` flags don't exist.
      popupBridgeTimeout: 180_000,
    },
  };
}

function createEntry(tenantId: string): RegistryEntry {
  const instance = new PublicClientApplication(buildConfig(tenantId));
  // B.SMOKE.8 fix — after initialize(), MSAL must consume the URL hash
  // left behind by loginRedirect. Without handleRedirectPromise(), the
  // SPA returns from login.microsoftonline.com with #code=... still in
  // the URL and the active account never gets set. Microsoft's docs:
  // "handleRedirectPromise should be invoked on every page load."
  const initPromise = instance
    .initialize()
    .then(async () => {
      try {
        const result = await instance.handleRedirectPromise();
        if (result !== null && result.account !== null) {
          instance.setActiveAccount(result.account);
        }
      } catch (err) {
        // Surface but don't reject — silent-acquire calls later will
        // throw their own InteractionRequiredAuthError if needed.
        console.warn(
          '[msalInstanceRegistry] handleRedirectPromise failed:',
          err,
        );
      }
    });
  const entry: RegistryEntry = {
    instance,
    refCount: 0,
    cleanupTimer: null,
    initPromise,
  };
  return entry;
}

/**
 * Acquire (or create) the MSAL instance for a portal tenant. Increments the
 * refcount and cancels any pending idle cleanup.
 *
 * Callers must pair every `acquireInstance` with a matching `releaseInstance`
 * (typically inside a React effect's cleanup function).
 */
export async function acquireInstance(
  lockedTenantId: string,
): Promise<PublicClientApplication> {
  const key = normalize(lockedTenantId);
  if (key.length === 0) {
    throw new Error('acquireInstance: lockedTenantId is required');
  }
  let entry = registry.get(key);
  if (!entry) {
    entry = createEntry(key);
    registry.set(key, entry);
  }
  if (entry.cleanupTimer !== null) {
    clearTimeout(entry.cleanupTimer);
    entry.cleanupTimer = null;
  }
  entry.refCount += 1;
  await entry.initPromise;
  return entry.instance;
}

/**
 * Decrement the refcount. If it drops to zero, schedule cleanup after
 * IDLE_CLEANUP_MS. Re-acquiring before the timer fires cancels cleanup.
 */
export function releaseInstance(lockedTenantId: string): void {
  const key = normalize(lockedTenantId);
  const entry = registry.get(key);
  if (!entry) {return;}
  entry.refCount = Math.max(0, entry.refCount - 1);
  if (entry.refCount === 0 && entry.cleanupTimer === null) {
    entry.cleanupTimer = setTimeout(() => {
      // Best-effort cache clear before removal so tokens don't linger.
      try {
        const account = entry.instance.getActiveAccount();
        if (account) {
          void entry.instance.clearCache({ account });
        }
      } catch {
        // Ignore — instance may not have been initialized properly.
      }
      registry.delete(key);
    }, IDLE_CLEANUP_MS);
  }
}

/** Test-only helper. Drops all entries + clears any pending timers. */
export function __resetRegistryForTests(): void {
  for (const entry of registry.values()) {
    if (entry.cleanupTimer !== null) {
      clearTimeout(entry.cleanupTimer);
      entry.cleanupTimer = null;
    }
  }
  registry.clear();
}

/** Test-only inspector. */
export function __peekRegistry(): Array<{
  tenantId: string;
  refCount: number;
  hasTimer: boolean;
}> {
  return Array.from(registry.entries()).map(([tenantId, entry]) => ({
    tenantId,
    refCount: entry.refCount,
    hasTimer: entry.cleanupTimer !== null,
  }));
}

/** Returns the active account on the tenant instance, or null. */
export function getActiveAccountForTenant(
  lockedTenantId: string,
): AccountInfo | null {
  const entry = registry.get(normalize(lockedTenantId));
  if (!entry) {return null;}
  const active = entry.instance.getActiveAccount();
  if (active) {return active;}
  const all = entry.instance.getAllAccounts();
  return all[0] ?? null;
}

/**
 * Reasonable defaults for `popupWindowAttributes`. MSAL's defaults render
 * a tiny window (~483×600 in Chrome) where the Microsoft sign-in UI ends
 * up cramped and the password field overflows. Center a 520×720 window
 * instead — wide enough for "Stay signed in?" prompts + the optional
 * email-only "Pick an account" carousel.
 *
 * Built lazily because vitest/node test envs don't define `window`.
 */
function popupWindow(): NonNullable<
  Parameters<PublicClientApplication['loginPopup']>[0]
>['popupWindowAttributes'] {
  const w = 520;
  const h = 720;
  const screenW = typeof window !== 'undefined' ? window.screen?.width ?? 1440 : 1440;
  const screenH = typeof window !== 'undefined' ? window.screen?.height ?? 900 : 900;
  return {
    popupSize: { width: w, height: h },
    popupPosition: {
      top: Math.max(0, Math.floor((screenH - h) / 2)),
      left: Math.max(0, Math.floor((screenW - w) / 2)),
    },
  };
}

/**
 * F16.L: Start the portal sign-in via MSAL **redirect** flow. The SPA
 * navigates away to login.microsoftonline.com; Microsoft sends the user
 * back to `redirectUri` with `#code=...`. The boot path in `main.tsx`
 * picks the response up via `handleRedirectPromise()`.
 *
 * Why redirect (not popup): modern Chrome + Microsoft's COOP headers
 * sever `window.opener` on the popup's first cross-origin navigation,
 * which blocks MSAL's popup-bridge handoff. The popup ends up stuck
 * showing our SPA root forever and the opener times out. Redirect
 * mode side-steps the whole opener mechanic.
 *
 * We persist `(slug, lockedTenantId, returnTo)` in sessionStorage
 * before starting the redirect so the boot-time handler can:
 *   - rebuild a PCA against the same tenant authority
 *   - know which portal route to navigate back to once the code
 *     exchange completes.
 */
export async function startPortalRedirectSignIn(args: {
  lockedTenantId: string;
  slug: string;
  returnTo: string;
}): Promise<void> {
  const key = normalize(args.lockedTenantId);
  const entry = registry.get(key);
  if (!entry) {
    throw new Error(
      `startPortalRedirectSignIn: no MSAL instance for tenant ${key}. ` +
        `Call acquireInstance() (or mount inside <MsalInstanceProvider>) first.`,
    );
  }
  await entry.initPromise;
  // Stash a recovery record before navigating away. The boot handler
  // reads this to rebuild the PCA + redirect back.
  try {
    sessionStorage.setItem(
      PORTAL_SIGNIN_PENDING_KEY,
      JSON.stringify({
        slug: args.slug,
        lockedTenantId: key,
        returnTo: args.returnTo,
        startedAt: new Date().toISOString(),
      }),
    );
  } catch {
    // sessionStorage unavailable; the boot handler will still try to
    // recover via the URL state param, but the slug/return mapping
    // will be lost.
  }
  await entry.instance.loginRedirect({ scopes: [...GRAPH_SCOPES] });
  // loginRedirect navigates away — this promise never resolves in the
  // normal path. Including the await for tail-recursion clarity.
}

/**
 * Pop a tenant-scoped sign-in dialog. Returns the AccountInfo on success.
 * The instance must already be acquired (the route's `MsalInstanceProvider`
 * acquires on mount).
 *
 * @deprecated F16.L — popup flow is broken on modern Chrome due to
 * COOP severing `window.opener`. Use `startPortalRedirectSignIn`.
 * Kept as a public export for the legacy `/` SPA + DiagnosticsTab.
 */
export async function connectPortalAccount(
  lockedTenantId: string,
): Promise<AccountInfo> {
  const key = normalize(lockedTenantId);
  const entry = registry.get(key);
  if (!entry) {
    throw new Error(
      `connectPortalAccount: no MSAL instance for tenant ${key}. ` +
        `Call acquireInstance() (or mount inside <MsalInstanceProvider>) first.`,
    );
  }
  await entry.initPromise;
  const result = await entry.instance.loginPopup({
    scopes: [...GRAPH_SCOPES],
    popupWindowAttributes: popupWindow(),
  });
  entry.instance.setActiveAccount(result.account);
  return result.account;
}

/** Clear the tenant cache + drop the active account. */
export async function disconnectPortalAccount(
  lockedTenantId: string,
): Promise<void> {
  const key = normalize(lockedTenantId);
  const entry = registry.get(key);
  if (!entry) {return;}
  await entry.initPromise;
  const account = entry.instance.getActiveAccount();
  if (account) {
    await entry.instance.clearCache({ account });
  }
  entry.instance.setActiveAccount(null);
}

/**
 * Acquire a Graph access token for a portal tenant. Tries silent first,
 * falls back to popup if MSAL signals interaction is required.
 */
export async function acquireGraphTokenForTenant(
  lockedTenantId: string,
): Promise<string> {
  const key = normalize(lockedTenantId);
  const entry = registry.get(key);
  if (!entry) {
    throw new Error(
      `acquireGraphTokenForTenant: no MSAL instance for tenant ${key}.`,
    );
  }
  await entry.initPromise;
  const account =
    entry.instance.getActiveAccount() ?? entry.instance.getAllAccounts()[0];
  if (!account) {
    throw new Error(
      'No Microsoft account connected for this portal. Call connectPortalAccount() first.',
    );
  }
  try {
    const result = await entry.instance.acquireTokenSilent({
      scopes: [...GRAPH_SCOPES],
      account,
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const result = await entry.instance.acquireTokenPopup({
        scopes: [...GRAPH_SCOPES],
        account,
        popupWindowAttributes: popupWindow(),
      });
      return result.accessToken;
    }
    throw err;
  }
}
